import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionTemplate, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { Check, Copy, Linkedin, Mail, MessageCircle } from "lucide-react";
import { CinematicButton } from "../CinematicButton";
import { LocalClock } from "../LocalClock";
import { RevealHeading } from "../RevealHeading";
import { links } from "../../data/portfolio";
import { media } from "../../data/media";
import { sectionHeading } from "../../utils/typography";
const channels = [{
  label: "Email",
  value: "s.m.zeeshan2233@gmail.com",
  href: links.email,
  icon: Mail,
  copy: "s.m.zeeshan2233@gmail.com"
}, {
  label: "WhatsApp",
  value: "+973 398 64230",
  href: links.whatsapp,
  icon: MessageCircle,
  copy: "+973 398 64230"
}, {
  label: "LinkedIn",
  value: "Syed Muhammad Zeeshan",
  href: links.linkedin,
  icon: Linkedin,
  copy: null
}];

/** Enquiry topics mirror the seven published capabilities. */
const topics = ["Art Direction", "3D / CGI", "VFX & compositing", "Motion graphics", "2D & 3D / explainer", "Video editing", "Creative direction", "UI UX / product", "B-Roll & Camera Shots"];
export function ContactSection() {
  const reduceMotion = useReducedMotion();
  const [topic, setTopic] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const copyTimeout = useRef<number>();
  useEffect(() => () => window.clearTimeout(copyTimeout.current), []);
  const lightX = useMotionValue(50);
  const lightY = useMotionValue(40);
  const smoothX = useSpring(lightX, {
    stiffness: 60,
    damping: 24
  });
  const smoothY = useSpring(lightY, {
    stiffness: 60,
    damping: 24
  });
  const spotlight = useMotionTemplate`radial-gradient(circle at ${smoothX}% ${smoothY}%, rgba(84,246,198,0.16), transparent 55%)`;
  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType !== "mouse" || reduceMotion) return;
    const rect = event.currentTarget.getBoundingClientRect();
    lightX.set((event.clientX - rect.left) / rect.width * 100);
    lightY.set((event.clientY - rect.top) / rect.height * 100);
  };
  const onCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
      window.clearTimeout(copyTimeout.current);
      copyTimeout.current = window.setTimeout(() => setCopied(null), 1800);
    } catch {
      setCopied(null);
    }
  };
  const mailHref = topic ? `${links.email}?subject=${encodeURIComponent(`Project enquiry: ${topic}`)}` : links.email;
  return <section id="contact" aria-labelledby="contact-heading" onPointerMove={onPointerMove} className="relative overflow-hidden px-5 py-24 md:px-8 md:py-32" data-id="element-94" data-mp="1ryfp4kcl9fey">
      <img src={media.ctaPlate} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-50" loading="lazy" data-id="element-95" data-mp="106zsdxhbicb0" />
      <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(5,8,8,0.97),rgba(5,8,8,0.6))]" data-id="element-96" data-mp="147jp4zxzbdwm" />
      {!reduceMotion && <motion.div aria-hidden="true" className="absolute inset-0" style={{
      background: spotlight
    }} data-id="element-97" data-mp="wok8v35te2hh" />}
      <div className="absolute inset-0 film-grain opacity-20" data-id="element-98" data-mp="rqsfs5tfoabp" />

      <div className="relative mx-auto max-w-[1480px]" data-id="element-99" data-mp="nnmbv4tsmv8e">
        <div className="flex flex-wrap items-center justify-between gap-3 font-mono text-[11px] uppercase tracking-[0.2em]" data-id="element-100" data-mp="nq8kz8wc1isq">
          <p className="flex items-center gap-4 text-[#54f6c6]" data-id="element-101" data-mp="dqrewqd652w8">009<span className="h-px w-10 bg-[#54f6c6]/40 sm:w-12" data-id="element-102" data-mp="drj2pf0ixwby" />Contact</p>
          <p className="flex items-center gap-2 text-white/45" data-id="element-103" data-mp="8ypwempeqfxr"><span className="dot-blink h-1.5 w-1.5 rounded-full bg-[#54f6c6]" data-id="element-104" data-mp="40emyd30y7g2" />Booking calls · Worldwide</p>
        </div>

        <RevealHeading as="h2" id="contact-heading" lines={["Let’s build high-impact", "visuals together"]} accent="high-impact" className={`mt-7 md:mt-9 ${sectionHeading}`} data-id="element-105" data-mp="3w4qu7dy0f4dx" />

        <div className="mt-10 grid gap-10 border-t border-white/15 pt-8 md:mt-12 lg:grid-cols-12 lg:gap-16" data-id="element-106" data-mp="3m6jh566ud6h7">
          <div className="lg:col-span-5" data-id="element-107" data-mp="1q3oh9ikfd5ez">
            <p className="max-w-sm text-base leading-8 text-white/70" data-id="element-108" data-mp="1v2hm6mlnbo3j">Ready to bring your project to life? Let’s talk.</p>

            <fieldset className="mt-8" data-id="element-109" data-mp="1gc21d8x56i5w">
              <legend className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/40" data-id="element-110" data-mp="1gaz9axo329oe">Start with a subject</legend>
              <div className="mt-5 flex flex-wrap gap-2" data-id="element-111" data-mp="17cn8y3xezxir">
                {topics.map(entry => {
                const selected = topic === entry;
                return <button key={entry} type="button" aria-pressed={selected} onClick={() => setTopic(selected ? null : entry)} data-cursor="Pick" className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm transition-[transform,background-color,border-color,color] duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] ${selected ? "border-[#54f6c6] bg-[#54f6c6] text-[#05100d]" : "border-white/15 bg-white/[0.035] text-white/70 hover:border-[#54f6c6]/60 hover:text-white"}`} data-id="element-112" data-mp="1bbnsxprmp67c">
                      <span aria-hidden="true" className={`h-1 w-1 rounded-full ${selected ? "bg-[#05100d]" : "bg-[#54f6c6]"}`} data-id="element-113" data-mp="13u7qlcxndtqt" />
                      {entry}
                    </button>;
              })}
              </div>
            </fieldset>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-114" data-mp="1dqks2wzfpbw4">
              <CinematicButton href={links.calendly} external cursor="Book" className="sm:min-w-[230px]" data-id="element-115" data-mp="18uvqyp7rnmxp">Book a call</CinematicButton>
              <CinematicButton href={mailHref} variant="secondary" cursor="Open" icon={<Mail className="h-4 w-4" data-id="element-117" data-mp="3ami8m05rq5hi" />} data-id="element-116" data-mp="3fm4nsf55pvsy">
                {topic ? "Email this brief" : "Email me"}
              </CinematicButton>
            </div>

            <p aria-live="polite" className="mt-5 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-118" data-mp="850jqftzeffa">
              {topic ? `Subject set / ${topic}` : "Pick a subject to prefill the email"}
            </p>
          </div>

          <div className="lg:col-span-7" data-id="element-119" data-mp="i49cj5d41o02">
            <ul className="grid gap-px bg-white/[0.08] sm:grid-cols-3" data-id="element-120" data-mp="i13o7scb2q5e">
              {channels.map((channel, index) => <motion.li key={channel.label} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.45,
              delay: index * 0.06
            }} className="group relative flex min-h-[150px] flex-col justify-between gap-6 bg-[#060a09] p-5 transition-colors duration-300 hover:bg-[#0a100f] md:min-h-[176px] md:p-6" data-id="element-121" data-mp="r3mmdz55celb" data-mpi={index}>
                  <span aria-hidden="true" className="absolute inset-x-0 top-0 h-px scale-x-0 bg-gradient-to-r from-transparent via-[#54f6c6]/70 to-transparent transition-transform duration-500 group-hover:scale-x-100" data-id="element-122" data-mp="2v9whv70utjuj" />

                  <channel.icon className="h-4 w-4 text-[#54f6c6]" aria-hidden="true" data-id="element-123" data-mp="2lb6z7c14vvd8" />

                  <div data-id="element-124" data-mp="2gdf5ccgxhz6e">
                    <span className="block font-mono text-[11px] uppercase tracking-[0.18em] text-white/40" data-id="element-125" data-mp="2ge73nv1wub0d">{channel.label}</span>
                    <a href={channel.href} target={channel.href.startsWith("mailto") ? undefined : "_blank"} rel="noreferrer" data-cursor="Open" className="mt-2 block break-words text-sm text-white transition-colors hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-126" data-mp="3e2qmb70uptki">
                      {channel.value}
                    </a>

                    {channel.copy && <button type="button" onClick={() => onCopy(channel.copy as string)} data-cursor="Copy" className="mt-3 inline-flex min-h-11 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45 transition-colors hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-127" data-mp="30clwv8nnwulo">
                        {copied === channel.copy ? <Check className="h-3.5 w-3.5" aria-hidden="true" data-id="element-128" data-mp="3o2ybf2dexpl8" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" data-id="element-129" data-mp="3o0c6lahwpo6p" />}
                        {copied === channel.copy ? "Copied" : `Copy ${channel.label.toLowerCase()}`}
                      </button>}
                  </div>
                </motion.li>)}
            </ul>

            <p role="status" aria-live="polite" className="sr-only" data-id="element-130" data-mp="1ypux30qci8pt">
              {copied ? `${copied} copied to clipboard` : ""}
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-131" data-mp="1w210rx80w1w4">
              <span className="flex items-center gap-2" data-id="element-132" data-mp="1r6bu79xl5kqb"><LocalClock data-id="element-133" data-mp="2540eognqqpuu" /> · your local time</span>
              <span data-id="element-134" data-mp="251ebsubsoyiy">Email · WhatsApp · LinkedIn</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}